import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import ls from 'local-storage';
import { __addResource } from 'apps/web_forms/expressions/utils';
import {
  newCachedRequest,
  getDeviceResources,
  getDeviceResource,
} from 'apps/web_forms/api/api';
import {
  getQualifiedResourceId,
  getResourceTooLargeKey,
  objectHasProperty,
} from 'apps/web_forms/utils/resourcesUtils';
import {
  LATEST,
  ACCEPTED_TYPES_FOR_GENERATED_PARAM,
} from 'apps/web_forms/utils/constants';
import { Parser } from 'apps/web_forms/expressions/jisonParser';

/**
 * @param {string} mimeType
 * @returns {boolean}
 */
const isNotImageAudioVideoOrBinary = (mimeType) => {
  return (
    !mimeType.includes('image/') &&
    !mimeType.includes('video/') &&
    !mimeType.includes('audio/') &&
    !mimeType.includes('application/octet-stream')
  );
};
let resourceListRequest;

const resourceRequests = {};
const resourceRequestCancelTokenSources = {};

const initialState = {
  resources: [],
  resourcesMap: {},
  resourceLoadCounter: 0,
  loadedResources: {},
  resourcesLoading: true,
};

const resourceSlice = createSlice({
  name: 'resource',
  initialState,
  reducers: {
    resetResourceSliceState: () => initialState,
    setResources(state, action) {
      state.resources = action.payload;
    },
    addItemToResourcesMap(state, action) {
      const { identifier, value } = action.payload;
      state.resourcesMap[identifier] = value;
    },
    addResourceData(state, action) {
      const { identifier, dataUrl } = action.payload;
      state.resourcesMap[identifier].url = dataUrl;
    },
    incrementResourceLoadCounter(state /*, action*/) {
      state.resourceLoadCounter = state.resourceLoadCounter + 1;
    },
    markResourceLoaded(state, action) {
      const qualifiedResourceId = action.payload;
      state.loadedResources[qualifiedResourceId] = true;
    },
    setResourcesLoading(state, action) {
      state.resourcesLoading = action.payload;
    },
  },
});

/**
 * Cache if possible. If an error is thrown attempting, mark (this
 * version) as too large to cache, to avoid future attempts.
 * @param cacheResource
 * @param {string} resourceKey
 * @param {string} tooLargeKey
 */
const cacheIfPossible = (cacheResource, resourceKey, tooLargeKey) => {
  const isTooLarge = ls.get(tooLargeKey);
  if (!isTooLarge) {
    const successfullyCached = ls.set(resourceKey, cacheResource);
    if (!successfullyCached) {
      ls.set(tooLargeKey, {
        version: cacheResource.version,
        isTooLarge: true,
      });
    }
  }
};

/**
 * Take resource as by version specified (latest version by default, as
 * specified by resource list response). If not cached already, fetch from
 * backend. Cache, if not too big. Store into memory to be available for
 * calculating expressions.
 * @param resourceToLoad
 */
const loadResource = async (resourceToLoad, dispatch) => {
  const versionToRetrieve = resourceToLoad.versionToUse;

  const resourceKey = getQualifiedResourceId(
    resourceToLoad.identifier,
    versionToRetrieve
  );
  const tooLargeKey = getResourceTooLargeKey(
    resourceToLoad.identifier,
    versionToRetrieve
  );

  if (resourceKey && isNotImageAudioVideoOrBinary(resourceToLoad.mime_type)) {
    let cacheResource = ls.get(resourceKey);
    const isLatest = versionToRetrieve === resourceToLoad.latestVersion;
    const latestResourceKey = getQualifiedResourceId(
      resourceToLoad.identifier,
      LATEST
    );
    const latestTooLargeKey = getResourceTooLargeKey(
      resourceToLoad.identifier,
      LATEST
    );

    if (!cacheResource) {
      const resourceData = await newCachedRequest({
        qualifiedResourceId: resourceKey,
        fetchId: resourceToLoad.id,
        optionalVersion: versionToRetrieve,
        generated: ACCEPTED_TYPES_FOR_GENERATED_PARAM.includes(
          resourceToLoad.mime_type
        ),
      });

      if (resourceData) {
        cacheResource = {
          version: versionToRetrieve,
          description: resourceToLoad.description,
          content: resourceData,
        };

        cacheIfPossible(cacheResource, resourceKey, tooLargeKey); // cache by version #
        if (isLatest) {
          cacheIfPossible(cacheResource, latestResourceKey, latestTooLargeKey); // cache as latest
        }
      }
    }

    dispatch(addResource(resourceKey, cacheResource?.content));
    if (isLatest) {
      dispatch(addResource(latestResourceKey, cacheResource?.content));
    }
  }
};

/**
 * @param {Array<any>} resources - Resource list items
 * @param {Object.<string, number>} resourceVersions - Version numbers by resource id
 */
const resourcesByVersion = (resources, resourceVersions) => {
  return resources.map((resource) => {
    const versionToUse = objectHasProperty(
      resourceVersions || {},
      resource.identifier
    )
      ? String(resourceVersions?.[resource.identifier])
      : resource.version;
    return {
      ...resource,
      versionToUse,
      latestVersion: resource.version,
    };
  });
};

export const addResource = (key, value) => async (dispatch) => {
  __addResource(key, value);
  dispatch(incrementResourceLoadCounter());
};

/**
 * @param {Array<string>} optionalSubset - List of resource ids
 * @param {Object.<string, number>} draftResourceVersions - Version numbers by resource id
 */
export const fetchResources =
  (optionalSubset, draftResourceVersions) => async (dispatch) => {
    try {
      resourceListRequest = new Promise((resolve) => {
        getDeviceResources(optionalSubset).then((resourceList) => {
          dispatch(setResources(resourceList));
          resolve(resourceList);
        });
      });

      const resources = await resourceListRequest;

      const resourcesToLoad = !optionalSubset
        ? resources
        : resources.filter((resourceToLoad) =>
            optionalSubset.includes(resourceToLoad.identifier)
          );
      const resourcesToLoadByVersion = resourcesByVersion(
        resourcesToLoad,
        draftResourceVersions
      );
      resourcesToLoadByVersion.forEach((resource) => {
        loadResource(resource, dispatch);
      });
      dispatch(setResourcesLoading(false));
      return resourcesToLoadByVersion;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return null;
    }
  };

export const fetchResourceInfo = (identifier) => async (dispatch) => {
  try {
    //TODO: Fix this - duplicated code from fetchResources
    const resourceListRequest = await new Promise((resolve) => {
      getDeviceResources([identifier]).then((resourceList) => {
        dispatch(setResources(resourceList));
        resolve(resourceList);
      });
    });

    const resourceListItem = await resourceListRequest.find(
      (r) => r.identifier === identifier
    );

    dispatch(
      addItemToResourcesMap({
        identifier,
        value: {
          id: resourceListItem.id,
          fileName: resourceListItem.description,
          mimeType: resourceListItem.mime_type,
        },
      })
    );

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    return null;
  }
};

export const fetchResourceData =
  (identifier, optionalVersion) => async (dispatch, getState) => {
    try {
      const currentResource = getState().resource.resourcesMap[identifier];
      if (currentResource.url) return;

      if (resourceRequests[identifier]) return;

      const cancelTokenSource = axios.CancelToken.source();
      resourceRequestCancelTokenSources[identifier] = cancelTokenSource;

      resourceRequests[identifier] = getDeviceResource({
        resourceId: currentResource.id,
        cancelToken: cancelTokenSource.token,
        version: optionalVersion,
        generated: ACCEPTED_TYPES_FOR_GENERATED_PARAM.includes(
          currentResource.mimeType
        ),
      });

      const resourceData = await resourceRequests[identifier];

      if (!resourceData) return;

      const blobObject = new Blob([resourceData], {
        type: currentResource.mimeType,
      });

      dispatch(
        addResourceData({
          identifier,
          dataUrl: URL.createObjectURL(blobObject),
        })
      );
      return;
    } catch (error) {
      // TODO: Integrate Bugsnag to log the errors in prod.
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

export const cancelResourceRequest = (identifier) => {
  const cancelTokenSource = resourceRequestCancelTokenSources[identifier];

  if (cancelTokenSource) {
    cancelTokenSource.cancel();
    delete resourceRequests[identifier];
    delete resourceRequestCancelTokenSources[identifier];
  }
};

export const {
  resetResourceSliceState,
  setResources,
  addItemToResourcesMap,
  addResourceData,
  incrementResourceLoadCounter,
  markResourceLoaded,
  addTestResource,
  setResourcesLoading,
} = resourceSlice.actions;
export default resourceSlice.reducer;
